import { Component, computed, effect, inject, input, output, resource } from "@angular/core";
import { HttpRequestOptions } from "../../dataaccess/data.access";
import { CalendarService } from "../../modules/home/calendar/calendar.service";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25Icons } from "../../modules/s25-icon/s25.icon.const";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { OptBean } from "../../modules/s25-opt/s25.opt.component";
import { OptUtil, OptUtilI } from "../../modules/s25-opt/s25.opt.util";
import { Item } from "../../pojo/Item";
import { UserprefService } from "../../services/userpref.service";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25CalendarStore } from "./calendar.store";
import { CalendarTable } from "./calendar.table.component";

@Component({
    selector: "s25-ng-calendar",
    standalone: true,
    imports: [CalendarTable, S25LoadingInlineModule, S25IconComponent],
    host: {
        class: "ngCalendarContainer ngCalendarPadding",
    },
    styles: `
        :host {
            display: block;
            width: 100%;
            font-size: 0.875rem;
        }
        .loading-indicator {
            display: flex;
            width: 100%;
            justify-content: center;
            font-size: 150%;
            padding: 0 1em 1em;
        }
        .ngCalArrowRight,
        .ngCalArrowLeft {
            cursor: pointer;
            margin: 0.5em auto;
            height: 2em;
            padding: 0 0.75em;
        }
        .ngCalArrowRight {
            float: right;
        }
        :host-context(.nm-party--on) :host {
            .ngCalArrowRight,
            .ngCalArrowLeft {
                background-color: transparent;
                border-color: var(--color--link-text);
                color: var(--color--link-text);
                cursor: pointer;

                &:hover,
                &:focus {
                    background-color: var(--color-primary-darker) !important;
                    color: var(--color--link-text-hover) !important;
                }
                &:hover {
                    border-color: transparent !important;
                }
                &:focus {
                    border-color: var(--color--link-text-hover) !important;
                }
            }
        }
    `,
    template: `
        <!-- TODO: render empty cells with skeleton loaders -->
        @if (isLoading() || rawCalData.isLoading() || blackouts.isLoading()) {
            <s25-ng-loading-inline-static class="loading-indicator" text="Calendar Loading..." />
        }
        @if (calData()?.events) {
            <s25-ng-calendar-table
                [events]="calData().events"
                [weekdays]="calData().weekday"
                [daysPerRow]="calData().days_per_row"
                [date]="date()"
            />
            <button
                class="btn btn-default ngOptArrowLeft ngCalArrowLeft"
                type="button"
                (click)="previousPage.emit()"
                aria-label="Update Date Backwards"
            >
                <s25-ng-icon [type]="icons.arrowLeft"></s25-ng-icon>
            </button>
            <button
                class="btn btn-default ngOptArrowRight ngCalArrowRight"
                type="button"
                (click)="nextPage.emit()"
                aria-label="Update Date Forwards"
            >
                <s25-ng-icon [type]="icons.arrowRight"></s25-ng-icon>
            </button>
        }
        @if (calMessage()) {
            <div class="NoInformation">
                <span>{{ calMessage() }}</span>
            </div>
        }
    `,
})
export class Calendar {
    readonly comptype = input.required<OptUtil.CompType>();
    readonly compsubject = input.required<Item.Subject>();
    readonly date = input.required<Date>();
    readonly searchQuery = input<string>();
    readonly dataFn =
        input<
            (params: CalendarService.DataParams, httpOptions: HttpRequestOptions) => Promise<{ root: CalendarData }>
        >();
    readonly showBlackouts = input<boolean>(false);
    readonly multiQueryStr = input<OptBean["multiQueryStr"]>();
    readonly useCache = input<OptBean["useCache"]>();
    readonly includeAddtl = input<OptBean["includeAddtl"]>();
    readonly showRelatedEvents = input<OptBean["showRelatedEvents"]>();
    readonly showRelatedLocations = input<OptBean["showRelatedLocations"]>();
    readonly endDate = input<OptBean["endDate"]>();
    readonly datePropertyValue = input<OptUtilI["datePropertyValue"]>();
    readonly chosenVal = input<OptBean["chosen"]["obj"]["val"]>();
    readonly isLoading = input<false>();

    readonly previousPage = output<void>();
    readonly nextPage = output<void>();
    readonly updated = output<OptUtilI["lastupdate"]>();

    readonly store = inject(S25CalendarStore);

    readonly weekstart = s25Resource({
        loader: () => UserprefService.getWeekstart(),
    });
    readonly calMessage = computed(() => {
        if (this.rawCalData.hasValue() && !this.rawCalData.value().root?.events?.length) {
            return "No search results were found.";
        }
    });

    // TODO: consider adding a sticky function that preserves the previous value while loading
    //  like in https://www.reddit.com/r/Angular2/comments/1kwr7db/resource_keep_previous_value_helper/
    //  if we want to reduce visual flickering by showing the old calendar data while loading new data
    readonly rawCalData = resource({
        params: () => {
            if (this.isLoading()) return undefined;
            return {
                modelBean: {
                    searchQuery: this.searchQuery(),
                    optBean: {
                        multiQueryStr: this.multiQueryStr(),
                        useCache: this.useCache(),
                        compsubject: this.compsubject(),
                        includeAddtl: this.includeAddtl(),
                        showRelatedEvents: this.showRelatedEvents(),
                        showRelatedLocations: this.showRelatedLocations(),
                        date: this.date(),
                        endDate: this.endDate(),
                        datePropertyValue: this.datePropertyValue(),
                        chosen: {
                            obj: {
                                val: this.chosenVal(),
                            },
                        },
                    },
                },
                weekstart: this.weekstart.value(),
                comptype: this.comptype(),
                dataFn: this.dataFn() ?? CalendarService.getData,
            };
        },
        loader: ({ params, abortSignal }): Promise<{ root: CalendarData }> =>
            params.dataFn(params, {
                abortSignal: abortSignal,
                throwErrorClass: true,
            }),
    });
    readonly blackouts = resource({
        params: () => {
            if (this.showBlackouts()) {
                return {
                    comptype: this.comptype(),
                    weekstart: this.weekstart.value(),
                    modelBean: {
                        optBean: {
                            // Use this instead of Pick<OptBean, ...> to ensure that the property is passed (even if it has no value,
                            multiQueryStr: this.multiQueryStr(),
                            useCache: this.useCache(),
                            compsubject: this.compsubject(),
                            includeAddtl: this.includeAddtl(),
                            showRelatedEvents: this.showRelatedEvents(),
                            showRelatedLocations: this.showRelatedLocations(),
                            date: this.date(),
                            endDate: this.endDate(),
                        },
                        searchQuery: this.searchQuery(),
                    },
                };
            }
        },
        loader: ({ params }) => CalendarService.getBlackouts(params).then((res) => res.blackout ?? []),
    });
    readonly hasMore = s25Resource({
        params: () => {
            if (
                this.rawCalData.hasValue() &&
                this.rawCalData.value().root.obj_id &&
                this.rawCalData.value().root.events?.length
            ) {
                return {
                    weekstart: this.weekstart.value(),
                    modelBean: {
                        optBean: {
                            compsubject: this.compsubject(),
                            date: this.date(),
                            endDate: this.endDate(),
                        },
                        data: { root: { root: { obj_id: this.rawCalData.value().root.obj_id } } },
                    },
                };
            }
        },
        loader: ({ params }) => CalendarService.getHasMore(params),
    });
    readonly calData = computed(() => {
        if (this.rawCalData.hasValue()) {
            let calData = this.rawCalData.value().root;
            calData = this.applyHasMore(calData);
            calData = this.applyBlackouts(calData);
            return calData;
        }
    });

    protected readonly icons = S25Icons.names;

    constructor() {
        effect(() => {
            if (this.rawCalData.hasValue()) {
                this.updated.emit(this.rawCalData.value().root.lastupdate);
            }
        });
        effect(() => this.store.setCalData(this.calData()));
        effect(() => {
            const error = this.rawCalData.error() || this.blackouts.error();
            if (error) {
                console.error(error);
                // keep UX minimal without S25LoadingApi, but surface error
                S25Util.showError(error, "There was an error processing your request");
            }
        });
    }

    /**
     * Add HasMore data to calData, without mutating the source
     */
    private applyHasMore(rawCalData: CalendarData): CalendarData {
        if (this.hasMore.value() && rawCalData.events?.length) {
            const hasMore = this.hasMore.value().hasMore;
            if (hasMore?.length !== rawCalData.events.length) {
                console.error("Unexpected mismatch between hasMore and events length");
                return rawCalData;
            }
            return {
                ...rawCalData,
                events: rawCalData.events.map((event, i) => ({
                    ...event,
                    hasMore: this.hasMore.value().hasMore[i] ?? 0,
                })),
            };
        } else {
            return rawCalData;
        }
    }

    private applyBlackouts(rawCalData: CalendarData): CalendarData {
        if (this.blackouts.hasValue() && rawCalData.events?.length) {
            const result = {
                ...rawCalData,
                // clone events to avoid mutating source. event.rsrv will be replaced
                events: rawCalData.events.map((event) => ({ ...event })),
            };
            const blackoutsClone = {
                blackout: [
                    // this array will be reversed, so clone it
                    ...this.blackouts.value().map((blackout) => ({
                        ...blackout,
                        // this array will be reversed, so clone it
                        date: [...blackout.date],
                    })),
                ],
            };
            CalendarService.setBlackouts(blackoutsClone, result, true);
            return result;
        } else {
            return rawCalData;
        }
    }

    public refresh() {
        this.rawCalData.reload();
    }
}
